"use client";

import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import { EventsListTable } from "../../../components/admin/events/EventsListTable";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import { getAllEvents } from "../../../services/eventsServices";
import { RotateCcw } from "lucide-react";
import { getCookie } from "../../../utils";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { Pagination } from "../../../components/admin/common/PaginationNumber";

const PendingEvents = () => {
  const topRef = useRef(null);

  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    hasPrev: false,
  });

  const statusFilter = "pending";
  const approvalStatusFilter = "pending";

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "events",
      currentPage,
      entriesOption.value,
      statusFilter,
      approvalStatusFilter,
    ],
    queryFn: () =>
      getAllEvents({
        page: currentPage,
        limit: entriesOption.value,
        status: statusFilter,
        approvalStatus: approvalStatusFilter,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.data) {
      setEvents(data.data.data);
      setPagination({
        totalPages: data.data.pagination.totalPages || 1,
        totalCount: data.data.pagination.totalCount || 0,
        hasMore: data.data.pagination.hasMore || false,
        hasPrev: data.data.pagination.hasPrev || false,
      });
    }
  }, [data]);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setCurrentPage(pageNumber);
      scrollToTop();
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentPage(1);
    scrollToTop();
  };

  const userId = getCookie("userId");
  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions("Events", userId, {
    enabled: role === "maintainer" && userId && userId !== "undefined",
  });

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              {/* Reference element at the top */}
              <div ref={topRef}></div>

              <Header
                title="Pending Events"
                desc="Review and manage pending events before they go live"
              />

              {/* Entries per page selector */}
              <div className="mb-4 flex justify-between items-center">
                <DropdownSelect
                  selected={entriesOption}
                  onChange={handleEntriesChange}
                />

                {/* Show pagination info */}
                {pagination.totalCount > 0 && (
                  <div className="text-sm text-gray-600">
                    Showing page {currentPage} of {pagination.totalPages}(
                    {pagination.totalCount} total events)
                  </div>
                )}
              </div>

              {isLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Loading events...</span>
                </div>
              )}

              {isError && (
                <div className="text-red-500 text-center mt-4">
                  <p>Error loading events: {error.message}</p>
                  <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-4 py-2 mt-2 bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200 mx-auto"
                  >
                    <RotateCcw size={16} />
                    Retry
                  </button>
                </div>
              )}

              {events && !isLoading && (
                <EventsListTable
                  events={events}
                  refetch={refetch}
                  currentIndex={(currentPage - 1) * entriesOption.value} // Calculate starting index
                  entriesPerPage={entriesOption.value}
                  permissionsData={permissionsData?.data ?? null}
                />
              )}

              <div className="h-5" />

              {/* Use your modern Pagination component */}
              {pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    totalPages={pagination.totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    showPages={5}
                  />
                </div>
              )}

              <div className="h-5" />
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default PendingEvents;
